import { Box, Button, Center, Image, Pressable, VStack } from 'native-base'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { MagnifyingGlassPlus } from '@phosphor-icons/react'

import { ProofOfServicePhotoUrl } from '@consumer/graphql'
import { useScreenSize } from '../utils/displayUtils'
import { colors } from '../utils/theme/configureTheme'
import ExperienceRatingSmiley from './ExperienceRatingSmiley'

interface DeliveryPhotoWithRatingProps {
  packageUrl: ProofOfServicePhotoUrl
  trackingId: string
  photoModalVisible: boolean
  setPhotoModalVisible: (visible: boolean) => void
  existingCsatRating?: number
  onSaveCsatRating: (rating: number) => void
  /** When true, hides the photo thumbnail (used when a carousel already shows photos above) */
  hidePhoto?: boolean
}

/**
 * DeliveryPhotoWithRating component displays a delivery photo with an optional customer satisfaction rating and a button to report an issue.
 * @param props - The component props.
 * @param props.packageUrl - The URL of the delivery photo to display.
 * @param props.trackingId - The tracking ID associated with the delivery, used for navigation when reporting an issue.
 * @param props.photoModalVisible - A boolean indicating whether the photo modal is currently visible.
 * @param props.setPhotoModalVisible - A function to toggle the visibility of the photo modal.
 * @param props.existingCsatRating - An optional number representing the existing customer satisfaction rating, if available.
 * @param props.onSaveCsatRating - A callback function that is triggered when a new customer satisfaction rating is saved, receiving the rating as an argument.
 * @param props.hidePhoto - An optional boolean that, when true, hides the photo thumbnail (useful when a carousel already displays photos above).
 * @returns JSX.Element representing the delivery photo with rating and report issue button.
 */
export default function DeliveryPhotoWithRating({
  packageUrl,
  trackingId,
  photoModalVisible,
  setPhotoModalVisible,
  existingCsatRating,
  onSaveCsatRating,
  hidePhoto = false,
}: DeliveryPhotoWithRatingProps) {
  const navigate = useNavigate()
  const screenSize = useScreenSize()

  const calculatePhotoCardWidth = useCallback(() => {
    if (screenSize.width > 500) {
      return '70%'
    } else if (screenSize.width <= 500 && screenSize.width > 390) {
      return '90%'
    } else {
      return '100%'
    }
  }, [screenSize.width])

  return (
    <VStack
      backgroundColor={colors.brand.white}
      width={calculatePhotoCardWidth()}
      alignItems="center"
      alignSelf="center"
      pb={3}
      pt={3}
      mt={3}
    >
      {!hidePhoto && (
        <VStack backgroundColor={colors.brand.lightGrey} width="95%">
          <Center backgroundColor="transparent">
            <Pressable onPress={() => setPhotoModalVisible(!photoModalVisible)}>
              <Box>
                <Image
                  src={packageUrl.url!}
                  size="sm"
                  mt={3}
                  mb={3}
                  borderRadius="4px"
                  width={255}
                  height={255}
                  resizeMode={'cover'}
                  style={{ overflow: 'hidden' }}
                  alt={'Proof of Service Photo'}
                />
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  style={{
                    backgroundColor: '#000000',
                    borderColor: '#21211F',
                    borderWidth: 1,
                    borderRadius: 20,
                    width: 30,
                    height: 30,
                    position: 'absolute',
                    top: 30,
                    right: 10,
                  }}
                >
                  <MagnifyingGlassPlus size={15} color="#FFFFFF" weight="bold" />
                </Box>
              </Box>
            </Pressable>
          </Center>
        </VStack>
      )}
      <VStack space={2} mt={2} alignItems="center">
        <ExperienceRatingSmiley
          onSmileyPress={smileyRating => {
            onSaveCsatRating(smileyRating)
          }}
          existingRating={existingCsatRating}
        />
        <Button
          onPress={() => navigate(`/reportIssue/${trackingId}`)}
          backgroundColor="transparent"
          borderWidth={2}
          borderColor={colors.vermillion[300]}
          _text={{
            color: colors.vermillion[300],
            fontWeight: '500',
            fontSize: '14px',
            lineHeight: '125%',
            letterSpacing: '-1%',
            fontFamily: 'Matter Regular',
          }}
          py={2}
          px={4}
          width="95%"
          maxWidth="350px"
        >
          Report an Issue
        </Button>
      </VStack>
    </VStack>
  )
}
